import React, { useState, useMemo } from 'react';
import ToolShell, { ACCENT, Field, input, card } from '../tools/ToolShell';

/*
 * PUBLIC, no-login lead-magnet tool: /tools/engagement-rate-calculator
 * Targets "engagement rate calculator" / "instagram engagement rate". Creator
 * enters followers + avg likes/comments -> ER% + a benchmark verdict by platform
 * and follower tier. Funnels to CreatorKhata (engagement = what brands pay for).
 */

const PLATFORMS = ['Instagram', 'YouTube', 'X / Twitter'];

// Rough "good" ER bands by platform (engagement / followers, %). Smaller accounts
// naturally see higher rates, so we grade against a tier-adjusted target.
function verdict(platform, followers, er) {
  let good = platform === 'Instagram' ? 3 : platform === 'YouTube' ? 5 : 0.5;
  if (followers > 100000) good *= 0.6;
  else if (followers < 10000) good *= 1.4;
  if (er >= good * 1.3) return { tag: 'Excellent', color: '#1a7a4a', note: 'Well above average — a strong selling point in your pitch deck.' };
  if (er >= good) return { tag: 'Good', color: '#2a7d52', note: 'Above the typical benchmark for your size. Brands like this.' };
  if (er >= good * 0.6) return { tag: 'Average', color: '#b8860b', note: 'Around the benchmark. Lead with niche/quality, not just reach.' };
  return { tag: 'Below average', color: '#b04a3a', note: 'Lower than typical — focus on content that sparks comments & saves.' };
}

export default function EngagementToolPage() {
  const [platform, setPlatform] = useState('Instagram');
  const [followers, setFollowers] = useState('');
  const [likes, setLikes] = useState('');
  const [comments, setComments] = useState('');

  const r = useMemo(() => {
    const f = parseInt(followers, 10) || 0;
    const eng = (parseInt(likes, 10) || 0) + (parseInt(comments, 10) || 0);
    if (!f || !eng) return null;
    const er = (eng / f) * 100;
    return { er, v: verdict(platform, f, er) };
  }, [platform, followers, likes, comments]);

  return (
    <ToolShell
      seo={{
        title: 'Engagement Rate Calculator (Instagram & YouTube) — CreatorKhata',
        description: 'Free engagement rate calculator for creators. Enter your followers, likes and comments to get your ER% and see how it compares to benchmarks brands look for.',
        path: '/tools/engagement-rate-calculator',
      }}
      h1="Engagement Rate Calculator"
      sub="Brands care more about engagement than follower count. Enter your numbers to get your ER% and see how you stack up against benchmarks. No signup."
      cta={{
        heading: 'A great engagement rate deserves a great rate card.',
        body: 'CreatorKhata turns your stats into a polished media kit and rate card, then manages the deal from pitch to payment. Put your engagement to work.',
      }}
    >
      {/* Inputs */}
      <div style={card()}>
        <Field label="Platform">
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {PLATFORMS.map(p => (
              <button key={p} onClick={() => setPlatform(p)} style={{
                flex: 1, padding: '0.5rem 0.3rem', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem',
                border: `1.5px solid ${platform === p ? ACCENT : '#e0e0e0'}`,
                background: platform === p ? ACCENT : 'white', color: platform === p ? 'white' : '#666',
              }}>{p}</button>
            ))}
          </div>
        </Field>
        <Field label="Followers / subscribers"><input style={input} type="number" value={followers} onChange={e => setFollowers(e.target.value)} placeholder="e.g. 50000" /></Field>
        <Field label="Average likes per post"><input style={input} type="number" value={likes} onChange={e => setLikes(e.target.value)} placeholder="e.g. 2200" /></Field>
        <Field label="Average comments per post"><input style={input} type="number" value={comments} onChange={e => setComments(e.target.value)} placeholder="e.g. 140" /></Field>
        <p style={{ fontSize: '0.72rem', color: '#aaa', margin: '0.25rem 0 0', lineHeight: 1.5 }}>
          ER = (avg likes + comments) ÷ followers × 100. Benchmarks are tier-adjusted — smaller accounts naturally see higher rates than large ones.
        </p>
      </div>

      {/* Output */}
      <div style={card()}>
        {!r ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📊</div>
            Enter your <b style={{ color: ACCENT }}>followers and likes</b> on the left — your engagement rate appears here <b>instantly</b>. No submit button.
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#888', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Your engagement rate</div>
            <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '3rem', color: ACCENT, lineHeight: 1.1 }}>
              {r.er.toFixed(2)}%
            </div>
            <div style={{ display: 'inline-block', marginTop: '0.5rem', padding: '0.35rem 0.9rem', borderRadius: 999, background: r.v.color + '18', color: r.v.color, fontWeight: 800, fontSize: '0.9rem' }}>
              {r.v.tag}
            </div>
            <p style={{ color: '#555', fontSize: '0.9rem', marginTop: '1rem', lineHeight: 1.5 }}>{r.v.note}</p>
            <div style={{ marginTop: '0.5rem', background: '#f1f8f4', border: `1px solid ${ACCENT}22`, borderRadius: 10, padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#2a5d42', textAlign: 'left' }}>
              💡 Put this number front-and-centre in your media kit. A strong ER lets you charge more than bigger creators with passive audiences.
            </div>
          </div>
        )}
      </div>
    </ToolShell>
  );
}
